import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MdArrowDownward, MdArrowUpward, MdAutoFixHigh, MdChevronRight, MdEventNote, MdSearch, MdUploadFile, MdWarningAmber } from "react-icons/md"
import { MobergTheme } from "../../Components/MobergThemes/MobergColors"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../Components/MobergButton/MobergButton"
import { MobergDropdown, MobergDropdownSize } from "../../Components/MobergDropdown/MobergDropdown"
import { MobergLogoSpinner } from "../../Components/MobergLogoSpinner/MobergLogoSpinner"
import { useAuthProvider } from "../../Providers/AuthProvider"
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider"
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks"
import claraLogo from "../../assets/images/clara-logo.png"
import { fmtDay } from "../Internal/claraDocumentReview"
import { attachSummary, batchFiles } from "../Internal/claraOriginals"
import {
	BANNER_ALL, PAGE_SIZE, confirmAssignments, listGroups, listQuery, shortDay, statusOf, views,
} from "./claraDocumentsList"
import { isActive, uploadQueue } from "./claraUploadQueue"
import { TypeBadge } from "./ClaraDocumentPanel"
import { ClaraUploadDialog, enqueueFiles, useUploadItems } from "./ClaraUploadDialog"
import { StatusIcon } from "./StatusIcon"
import {
	Banner, Buttons, Center, ColHead, DocRow, DocTitle, Empty, GroupHead, GroupHint, GroupLabel, GuessChip, HiddenInput, LinkButton,
	ListPane, Muted, PageContainer, PageTitle, SearchWrapper, SectionHeader, SortButton, Split, TinyButton, Toolbar,
	ViewButton, IconButton,
} from "./claraDocumentsStyles"

// The documents Clara quotes in this workspace, grouped by what needs doing. Anyone with Clara
// can browse them and open the PDFs; changing them is an admin's job (env:manage), which Clara
// enforces too. Each group loads its own pages, so a site with 2000 documents stays light.
const CLARA_API = window.env?.REACT_APP_CLARA_API_URL

const COL = {
	type: { width: 180, flexShrink: 0 },
	review: { width: 130, flexShrink: 0 },
	pdf: { width: 64, flexShrink: 0 },
	added: { width: 56, flexShrink: 0, textAlign: "right" },
}

const DOCUMENTS = FRONTEND_LINKS.SUBPAGES.CLARA.SUBPAGES.DOCUMENTS.MAIN
const GROUP_BY = [{ value: "status", label: "Status" }, { value: "type", label: "Type" }, { value: "none", label: "No grouping" }]
const SAVED_KEY = "clara-documents-list"

function readSaved() {
	try {
		return JSON.parse(window.sessionStorage.getItem(SAVED_KEY)) ?? {}
	} catch {
		return {}
	}
}

const POLL_MS = 3000
const DETECT_BATCH = 20
// Clara takes at most this many ids per bulk request.
const BULK_CHUNK = 500

function chunks(list, size = BULK_CHUNK) {
	return Array.from({ length: Math.ceil(list.length / size) }, (_, i) => list.slice(i * size, (i + 1) * size))
}

function ReviewText({ document }) {
	switch (document.review_status) {
		case "overdue":
			return <span style={{ ...COL.review, fontSize: 13, fontWeight: 600, color: "var(--color-danger-text-hover)" }}>{fmtDay(document.next_review_date)}</span>
		case "acknowledged":
		case "current":
			return <span style={{ ...COL.review, fontSize: 13 }}>{fmtDay(document.next_review_date)}</span>
		default:
			return <Muted style={{ ...COL.review, fontSize: 13 }}>No date</Muted>
	}
}

const EMPTY = { counts: {}, can_manage: false, indexing_available: true, originals_stored: false }

export default function ClaraDocuments() {
	const { getToken } = useAuthProvider()
	const { selectedWorkspace } = useWorkspacesProvider()
	const uploads = useUploadItems()

	const [meta, setMeta] = useState(EMPTY)
	const [groups, setGroups] = useState({})
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [notice, setNotice] = useState(null)
	const keepNotice = useRef(false)
	const [searchText, setSearchText] = useState("")
	const [q, setQ] = useState("")
	const [view, setView] = useState(() => readSaved().view ?? "all")
	const [groupBy, setGroupBy] = useState(() => readSaved().groupBy ?? "status")
	const [sort, setSort] = useState({ sort: "created_at", order: "desc" })
	const [pages, setPages] = useState({})
	const [collapsed, setCollapsed] = useState({})
	const navigate = useNavigate()
	const [uploadOpen, setUploadOpen] = useState(false)
	const [working, setWorking] = useState(null)
	const originalsInput = useRef(null)
	const latestFetch = useRef(0)
	const fetching = useRef(false)

	const canManage = meta.can_manage === true
	const counts = meta.counts ?? {}
	const sections = listGroups(groupBy, view, meta.originals_stored)

	const request = useCallback(async (path, options = {}) => {
		const response = await fetch(`${CLARA_API}${path}`, {
			...options,
			headers: { ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}), Authorization: getToken() },
		})
		const body = await response.json().catch(() => ({}))
		if (!response.ok) throw new Error(body.error || `status ${response.status}`)
		return body
	}, [getToken])

	const post = useCallback(
		(path, body) => request(path, { method: "POST", body: JSON.stringify({ workspace_name: selectedWorkspace, ...body }) }),
		[request, selectedWorkspace],
	)

	const sectionKey = sections.map(s => `${s.key}:${JSON.stringify(s.query)}`).join("|")
	const fetchList = useCallback(async () => {
		if (!selectedWorkspace || !CLARA_API) return
		// A search, a view change and the poll can all be in flight; only the latest may land.
		const mine = ++latestFetch.current
		fetching.current = true
		try {
			// Each section shows its first limit rows, read in pages of BANNER_ALL at once. Only the
			// first request carries the workspace counts.
			const loaded = await Promise.all(sections.map(async (section, index) => {
				const limit = (pages[section.key] ?? 1) * PAGE_SIZE
				const reads = Array.from({ length: Math.ceil(limit / BANNER_ALL) }, (_, i) => request(`/documents?${listQuery(selectedWorkspace, {
					...section.query, q, ...sort, page: i + 1, perPage: Math.min(limit, BANNER_ALL), counts: index === 0 && i === 0,
				})}`))
				const results = await Promise.all(reads)
				const seen = new Set()
				const documents = results.flatMap(r => r.documents ?? []).filter(d => !seen.has(d.id) && seen.add(d.id)).slice(0, limit)
				return [section.key, { documents, total: results[0]?.total ?? 0, result: results[0] }]
			}))
			if (mine !== latestFetch.current) return
			const first = loaded[0]?.[1].result
			if (first) setMeta(first)
			setGroups(Object.fromEntries(loaded.map(([key, { documents, total }]) => [key, { documents, total }])))
			setError(null)
		} catch (e) {
			if (mine === latestFetch.current) setError(`Could not load documents: ${e.message}`)
		} finally {
			if (mine === latestFetch.current) {
				fetching.current = false
				setLoading(false)
			}
		}
	}, [request, selectedWorkspace, sectionKey, q, sort, pages])

	useEffect(() => {
		fetchList()
	}, [fetchList])

	useEffect(() => {
		const timer = setTimeout(() => {
			if (searchText !== q) setQ(searchText)
		}, 300)
		return () => clearTimeout(timer)
	}, [searchText, q])

	useEffect(() => {
		setPages({})
	}, [selectedWorkspace])

	useEffect(() => {
		try {
			window.sessionStorage.setItem(SAVED_KEY, JSON.stringify({ view, groupBy }))
		} catch {
			// a private window may refuse storage; the list still works
		}
	}, [view, groupBy])

	const allDocuments = Object.values(groups).flatMap(g => g.documents)
	const openDocumentPage = document => navigate(`${DOCUMENTS}/${document.id}`, { state: { ids: allDocuments.map(d => d.id) } })

	// Poll while Clara is indexing, for the list and for this session's uploads, then stop.
	const indexingUploads = uploadQueue.indexing(selectedWorkspace)
	const busy = counts.queued > 0 || indexingUploads.length > 0
	useEffect(() => {
		if (!busy) return undefined
		const timer = setInterval(async () => {
			if (fetching.current) return
			const ids = uploadQueue.indexing(selectedWorkspace).map(item => item.documentId)
			// One page holds at most BANNER_ALL, so each group is only judged by its own reply.
			for (let start = 0; start < ids.length; start += BANNER_ALL) {
				const group = ids.slice(start, start + BANNER_ALL)
				try {
					const found = await request(`/documents?${listQuery(selectedWorkspace, { ids: group, perPage: BANNER_ALL })}`)
					uploadQueue.indexed(group, found.documents ?? [])
				} catch {
					// the next tick tries again
				}
			}
			fetchList()
		}, POLL_MS)
		return () => clearInterval(timer)
	}, [busy, fetchList, request, selectedWorkspace])

	useEffect(() => {
		if (!notice || keepNotice.current) return undefined
		const timer = setTimeout(() => setNotice(null), 6000)
		return () => clearTimeout(timer)
	}, [notice])

	// Uploads stop with the tab; say so before it closes.
	const uploading = uploads.some(isActive)
	useEffect(() => {
		if (!uploading) return undefined
		const warn = event => {
			event.preventDefault()
			event.returnValue = ""
		}
		window.addEventListener("beforeunload", warn)
		return () => window.removeEventListener("beforeunload", warn)
	}, [uploading])

	const refresh = useCallback(() => {
		fetchList()
	}, [fetchList])

	function sortBy(column) {
		setPages({})
		setSort(s => ({ sort: column, order: s.sort === column && s.order === "asc" ? "desc" : "asc" }))
	}

	async function run(label, action) {
		setWorking(label)
		setError(null)
		setNotice(null)
		keepNotice.current = false
		try {
			await action()
		} catch (e) {
			setError(`Could not ${label}: ${e.message}`)
		} finally {
			setWorking(null)
			refresh()
		}
	}

	const setTypes = assignments => run("change the type", async () => {
		let updated = 0
		for (const part of chunks(assignments)) updated += (await post("/documents/types", { assignments: part })).updated.length
		setNotice(`Set the type of ${updated} document${updated === 1 ? "" : "s"}.`)
	})

	const confirmAllGuesses = () => run("confirm the types", async () => {
		const unsure = []
		for (let page = 1; ; page++) {
			const found = await request(`/documents?${listQuery(selectedWorkspace, { type: "needs_type", page, perPage: BANNER_ALL })}`)
			unsure.push(...(found.documents ?? []))
			if (!found.documents?.length || unsure.length >= (found.total ?? 0)) break
		}
		const assignments = confirmAssignments(unsure)
		let updated = 0
		for (const part of chunks(assignments)) updated += (await post("/documents/types", { assignments: part })).updated.length
		const left = unsure.length - updated
		setNotice(`Confirmed the type of ${updated} document${updated === 1 ? "" : "s"}.${left ? ` ${left} had no guess; pick a type for each.` : ""}`)
	})

	const deleteOne = document => {
		if (!window.confirm(`Delete "${document.title}"? Clara will no longer be able to answer from it.`)) return
		run("delete", async () => {
			await post(`/documents/${document.id}/delete`, {})
		})
	}

	const detectTypes = () => run("detect types", async () => {
		let detected = 0
		let unsureCount = 0
		let left = 0
		let afterId = 0
		for (;;) {
			const result = await post("/documents/detect-types", { limit: DETECT_BATCH, after_id: afterId })
			detected += result.detected
			unsureCount += result.needs_type
			left = result.remaining
			setWorking(`detect types (${result.remaining} left)`)
			// Each batch starts after the last, so documents that keep failing cannot stop the rest,
			// and a short batch means every document was tried once.
			if (!result.remaining || result.detected + result.needs_type + result.failed < DETECT_BATCH) break
			afterId = result.last_id
		}
		const failedNote = left ? ` ${left} could not be read this time; try again later.` : ""
		keepNotice.current = Boolean(left)
		setNotice(`Clara set the type of ${detected} document${detected === 1 ? "" : "s"}. ${unsureCount} need${unsureCount === 1 ? "s" : ""} you to choose.${failedNote}`)
	})

	const rescanDates = () => run("read dates", async () => {
		const result = await post("/documents/rescan-dates", {})
		setNotice(result.scanned
			? `Clara looked for a review date in ${result.scanned} document${result.scanned === 1 ? "" : "s"} without one and found ${result.updated}.`
			: "Every document already has its review date, or Clara found none in it when it was uploaded.")
	})

	async function attachOriginals(event) {
		const files = Array.from(event.target.files ?? [])
		event.target.value = ""
		if (!files.length) return
		const total = { matched: [], unmatched: [], failed: [] }
		await run("attach originals", async () => {
			for (const batch of batchFiles(files)) {
				const body = new FormData()
				batch.forEach(file => body.append("files", file))
				const result = await request(`/documents/originals?workspace_name=${encodeURIComponent(selectedWorkspace)}`, { method: "POST", body })
				total.matched.push(...(result.matched ?? []))
				total.unmatched.push(...(result.unmatched ?? []))
				total.failed.push(...(result.failed ?? []))
			}
			keepNotice.current = true
			setNotice(attachSummary(total, files.length))
		})
	}

	if (!CLARA_API) {
		return <PageContainer><Banner $tone="error">Clara is not configured.</Banner></PageContainer>
	}

	if (loading) {
		return (
			<PageContainer>
				<Center>
					<MobergLogoSpinner />
				</Center>
			</PageContainer>
		)
	}

	const activeUploads = uploads.filter(isActive).length
	const sortIcon = column => sort.sort === column && (sort.order === "asc" ? <MdArrowUpward size={12} /> : <MdArrowDownward size={12} />)
	const detecting = working?.startsWith("detect types")
	const plural = (count, one, many) => (count === 1 ? one : many)
	const shown = sections.filter(section => (groups[section.key]?.total ?? 0) > 0)

	function groupAction(section) {
		if (!canManage) return null
		if (section.key === "type" || section.key === "needs_type") {
			return <LinkButton type="button" onClick={confirmAllGuesses} disabled={Boolean(working)} style={{ alignSelf: "center" }}>{working === "confirm the types" ? "Confirming…" : "Confirm all guesses"}</LinkButton>
		}
		if (section.key === "pdf") {
			return <LinkButton type="button" onClick={() => originalsInput.current?.click()} disabled={Boolean(working)} style={{ alignSelf: "center" }}>{working === "attach originals" ? "Attaching…" : "Attach PDFs"}</LinkButton>
		}
		return null
	}

	function row(document) {
		const status = statusOf(document, meta.originals_stored)
		const guess = canManage && document.needs_type && document.type_guess
		return (
			<DocRow key={document.id} data-document-id={document.id}>
				<StatusIcon kind={status} />
				<DocTitle type="button" onClick={() => openDocumentPage(document)}>
					<span>{document.title}</span>
					{document.status === "failed"
						? <em style={{ color: "var(--color-danger-text-hover)" }}>{document.error || "Indexing failed"}</em>
						: document.doc_number && <em>{document.doc_number}</em>}
				</DocTitle>
				{canManage && document.status === "failed" && (
					<LinkButton type="button" onClick={() => deleteOne(document)} disabled={Boolean(working)} style={{ alignSelf: "center" }}>Delete</LinkButton>
				)}
				<span style={{ ...COL.type, display: "inline-flex", alignItems: "center", gap: 6 }}>
					{guess ? (
						<>
							<GuessChip title="Clara's guess">{document.type_guess}?</GuessChip>
							<TinyButton type="button" onClick={() => setTypes([{ document_id: document.id, doc_type: document.type_guess }])} disabled={Boolean(working)} aria-label={`Confirm ${document.type_guess} for ${document.title}`}>
								Confirm
							</TinyButton>
						</>
					) : (
						<TypeBadge document={document} plain />
					)}
				</span>
				<ReviewText document={document} />
				<Muted style={{ ...COL.pdf, fontSize: 13 }}>{document.has_original || !meta.originals_stored ? "" : "No PDF"}</Muted>
				<Muted style={{ ...COL.added, fontSize: 13 }} title={document.created_at ? new Date(document.created_at).toLocaleString() : undefined}>{shortDay(document.created_at)}</Muted>
			</DocRow>
		)
	}

	return (
		<PageContainer>
			<SectionHeader>
				<div style={{ flex: "1 1 360px", display: "flex", alignItems: "center", gap: 10 }}>
					<img src={claraLogo} alt="" style={{ width: 28, height: 28 }} />
					<PageTitle>Clara Documents</PageTitle>
				</div>
				<SearchWrapper style={{ flex: "0 1 300px" }}>
					<MdSearch size={18} color="var(--text-secondary-color)" />
					<input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search title, file or number" aria-label="Search documents" />
				</SearchWrapper>
				{canManage && (
					<Buttons>
						{counts.undetected > 0 && (
							<MobergButton
								onClick={detectTypes}
								variant={MobergButtonVariant.OUTLINE}
								theme={MobergTheme.BLUE}
								shape={MobergButtonShape.RECTANGLE}
								disabled={Boolean(working) || !meta.indexing_available}
								tooltip="Clara reads each type from the title and first page. Types a person chose are never changed."
							>
								<MdAutoFixHigh size={16} /> {detecting ? "Detecting…" : `Detect types (${counts.undetected})`}
							</MobergButton>
						)}
						<MobergButton onClick={() => setUploadOpen(true)} variant={MobergButtonVariant.FILLED} theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE} disabled={!meta.indexing_available}>
							<MdUploadFile size={16} /> {activeUploads ? `Uploading ${activeUploads}…` : "Upload documents"}
						</MobergButton>
					</Buttons>
				)}
			</SectionHeader>
			<HiddenInput ref={originalsInput} type="file" accept="application/pdf,.pdf" multiple onChange={attachOriginals} />

			{canManage && !meta.indexing_available && (
				<Banner $tone="warn">
					<MdWarningAmber size={18} />
					Document indexing is unavailable: this deployment has no OpenAI API key configured. Uploading is disabled rather than
					producing documents that look indexed but cannot be searched.
				</Banner>
			)}
			{error && <Banner $tone="error">{error}</Banner>}
			{notice && <Banner $tone="info">{notice}</Banner>}
			{detecting && <Banner $tone="info">Detecting types, {working.replace("detect types", "").trim() || "starting"}…</Banner>}

			<Toolbar>
				{views(counts, view).map(v => (
					<ViewButton key={v.key} type="button" aria-pressed={view === v.key} $active={view === v.key} onClick={() => { setPages({}); setView(v.key) }}>
						{v.label}<span>{v.count}</span>
					</ViewButton>
				))}
				<span style={{ flexGrow: 1 }} />
				{canManage && counts.total > 0 && (
					<LinkButton type="button" onClick={rescanDates} disabled={Boolean(working)} title="Looks again for a review date in documents that have none" style={{ alignSelf: "center", display: "inline-flex", alignItems: "center", gap: 4 }}>
						<MdEventNote size={14} /> {working === "read dates" ? "Reading dates…" : "Find missing review dates"}
					</LinkButton>
				)}
				<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
					<Muted style={{ fontSize: 13 }}>Group by</Muted>
					<MobergDropdown
						options={GROUP_BY}
						selectedValue={groupBy}
						onChange={value => { setPages({}); setGroupBy(value) }}
						size={MobergDropdownSize.SMALL}
						width={150}
						ariaLabel="Group by"
					/>
				</span>
			</Toolbar>

			<Split>
				<ListPane aria-label="Documents">
					<ColHead>
						<SortButton type="button" $active={sort.sort === "title"} onClick={() => sortBy("title")} style={{ flex: "1 1 auto" }}>Document {sortIcon("title")}</SortButton>
						<span style={COL.type}>Type</span>
						<SortButton type="button" $active={sort.sort === "next_review_date"} onClick={() => sortBy("next_review_date")} style={COL.review}>Review by {sortIcon("next_review_date")}</SortButton>
						<span style={COL.pdf}>PDF</span>
						<SortButton type="button" $active={sort.sort === "created_at"} onClick={() => sortBy("created_at")} style={{ ...COL.added, justifyContent: "flex-end" }}>Added {sortIcon("created_at")}</SortButton>
					</ColHead>

					{shown.length === 0 && (
						<Empty>
							{counts.total
								? q ? "No documents match this search." : "Nothing here."
								: canManage
									? "No documents yet. Upload policy PDFs and Clara will be able to answer from them, quoting the page."
									: "No documents have been uploaded for this workspace yet."}
						</Empty>
					)}

					{shown.map(section => {
						const group = groups[section.key]
						const open = !collapsed[groupBy + section.key]
						return (
							<section key={section.key} aria-label={section.label}>
								{!section.flat && (
									<GroupHead>
										<IconButton
											type="button"
											onClick={() => setCollapsed(c => ({ ...c, [groupBy + section.key]: open }))}
											aria-expanded={open}
											aria-label={`${open ? "Hide" : "Show"} ${section.label}`}
											style={{ width: 28, height: 28 }}
										>
											<MdChevronRight size={18} style={{ transform: `rotate(${open ? 90 : 0}deg)`, transition: "transform 0.15s" }} />
										</IconButton>
										<StatusIcon kind={section.icon} />
										<GroupLabel>{section.label}</GroupLabel>
										<Muted style={{ fontSize: 13 }}>{group.total}</Muted>
										{section.hint && <GroupHint>{section.hint}</GroupHint>}
										<span style={{ flexGrow: 1 }} />
										{groupAction(section)}
									</GroupHead>
								)}
								{open && group.documents.map(row)}
								{open && group.documents.length < group.total && (
									<GroupHead as="div" style={{ background: "none" }}>
										<LinkButton type="button" onClick={() => setPages(p => ({ ...p, [section.key]: (p[section.key] ?? 1) + 1 }))} style={{ alignSelf: "center" }}>
											Show {Math.min(PAGE_SIZE, group.total - group.documents.length)} more of {group.total - group.documents.length}
										</LinkButton>
									</GroupHead>
								)}
							</section>
						)
					})}
				</ListPane>

			</Split>

			{counts.queued > 0 && <Muted style={{ fontSize: 13 }}>Clara is reading {counts.queued} {plural(counts.queued, "document", "documents")}. The list updates by itself.</Muted>}

			{uploadOpen && (
				<ClaraUploadDialog
					disabled={!meta.indexing_available}
					onClose={() => setUploadOpen(false)}
					onPick={files => enqueueFiles(files, { workspace: selectedWorkspace, getToken }).then(refresh)}
				/>
			)}
		</PageContainer>
	)
}
